'use client';

import type { FeatureServicesTabItem, FeatureServicesTabsProps } from '@/core/types';

import { m, AnimatePresence } from 'framer-motion';
import React, { useRef, useState, useEffect } from 'react';

import { Iconify } from '@/components/iconify';
import { Box, Image, Button, Container, Typography } from '@/components/ui';

import { RouterLink } from '@/routes/components';

export default function FeatureServicesTabs({
  title,
  description,
  items,
  className,
}: FeatureServicesTabsProps) {
  const [activeTab, setActiveTab] = useState(items[0]?.id || '');
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const handleTabClick = (tabId: string) => setActiveTab(tabId);
  const handleAccordionToggle = (itemId: string) =>
    setOpenAccordion(openAccordion === itemId ? null : itemId);

  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [indicatorTop, setIndicatorTop] = useState(0);

  const activeItem = items.find((item) => item.id === activeTab);

  useEffect(() => {
    const container = containerRef.current;
    const index = items.findIndex((item) => item.id === activeTab);
    const itemEl = itemRefs.current[index];

    if (container && itemEl) {
      const containerRect = container.getBoundingClientRect();
      const itemRect = itemEl.getBoundingClientRect();

      const center = itemRect.top - containerRect.top + itemRect.height / 2 - 12;

      setIndicatorTop(center);
    }
  }, [items, activeTab]);

  return (
    <section className={`relative w-full bg-white py-12 md:py-32 ${className}`}>
      <Container>
        {title && (
          <Typography variant="h2" className="text-center mb-8">
            {title}
          </Typography>
        )}

        {description && (
          <Typography variant="body1" className="text-center mb-12 max-w-3xl mx-auto">
            {description}
          </Typography>
        )}

        {/* Desktop: Vertical Tabs + Horizontal Indicator */}
        <div className="hidden lg:block z-10">
          <Box className="grid items-start grid-cols-[1fr_15px_1fr]">
            {/* Tabs */}
            <Box ref={containerRef} className="relative overflow-hidden flex flex-col pr-6 gap-4">
              {items.map((item, index) => (
                <ItemButton
                  ref={(el) => (itemRefs.current[index] = el)}
                  key={item.id}
                  item={item}
                  onClick={handleTabClick}
                  active={activeTab}
                />
              ))}
              <Box
                className="absolute w-6 h-6 -right-[12px] bg-emerald-400 rounded-full shadow-md transition-all duration-300"
                style={{ top: `${indicatorTop}px` }}
              />
            </Box>

            <Box className="relative overflow-hidden h-full">
              <Box className="z-10 relative w-[2px] h-full bg-violet rounded-full border-l-1 border-white" />
            </Box>
            {/* Content */}
            <Box className="pl-8">
              <AnimatePresence mode="wait">
                {activeItem && (
                  <m.div
                    key={activeItem.id}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                  >
                    <ItemContent item={activeItem} />
                  </m.div>
                )}
              </AnimatePresence>
            </Box>
          </Box>
        </div>

        {/* Mobile Accordion */}
        <div className="flex flex-col gap-4 z-10 lg:hidden">
          {items.map((item) => (
            <Box key={item.id} className="flex flex-col">
              <ItemButton item={item} onClick={handleAccordionToggle} active={openAccordion} />
              <AnimatePresence initial={false}>
                {openAccordion === item.id && (
                  <m.div
                    key="accordion"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <Box className="p-6">
                      <ItemContent item={item} />
                    </Box>
                  </m.div>
                )}
              </AnimatePresence>
            </Box>
          ))}
        </div>
      </Container>
    </section>
  );
}

interface ItemButtonProps {
  ref?: (el: any) => any;
  item: FeatureServicesTabItem;
  onClick: (itemId: string) => void;
  active: string | null;
}

const ItemButton: React.FC<ItemButtonProps> = ({ item, onClick, active, ...other }) => {
  const isActive = active === item.id;

  return (
    <button
      className={`w-full cursor-pointer text-left bg-violet/10 border-violet rounded-xl border-1 px-5 py-4 transition shadow-sm flex flex-col gap-1 items-start ${
        isActive
          ? 'shadow-md !bg-violet text-white'
          : ' hover:bg-violet hover:[&_p,&_h6]:text-white hover:[&_svg]:!text-white/50'
      }`}
      onClick={() => onClick(item.id)}
      {...other}
    >
      <Box className="flex w-full flex-col items-start">
        <Box className="flex w-full gap-2 justify-between">
          <Typography
            variant="h6"
            className={`font-semibold text-xl mb-2 ${isActive ? 'text-white' : ' text-gray-900'}`}
          >
            {item.title}
          </Typography>

          <Iconify
            icon={item.icon as any}
            width={28}
            height={32}
            className={`font-semibold mb-2 ${isActive ? 'text-white/50' : ' text-violet/50'}`}
          />
        </Box>
        <Typography
          variant="body2"
          component="div"
          className={`text-sm max-w-[95%] ${isActive ? 'text-white' : ' text-gray-500'}`}
        >
          <Box dangerouslySetInnerHTML={{ __html: item.description }} />
        </Typography>
      </Box>

      {/* Chevron for mobile */}
      <m.div
        className="mt-1 lg:hidden"
        animate={{ rotate: isActive ? 180 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <Iconify icon="lucide:chevron-down" width={24} height={24} />
      </m.div>
    </button>
  );
};

interface ItemContentProps {
  item: FeatureServicesTabItem;
}

const ItemContent: React.FC<ItemContentProps> = ({ item }) => (
  <Box>
    <Typography variant="h3" className="mb-3 text-primary text-2xl">
      {item.content.title}
    </Typography>

    <Typography
      variant="body1"
      component="div"
      className="text-gray-600 text-lg leading-[22px] mb-2 font-medium"
    >
      <Box dangerouslySetInnerHTML={{ __html: item.content.description }} />
    </Typography>

    <Box className="flex justify-center mb-2">
      <Image
        src={item.content.image.url}
        alt={item.content.image.alt}
        className="w-full h-auto"
        objectFit="contain"
        width={700}
        height={216}
        loading="lazy"
      />
    </Box>

    <Box className="mb-4">
      {item.content.benefits.map((benefit, index) => (
        <Box key={index} className="flex items-start p-1">
          <Iconify icon="lucide:check" className="text-primary mr-2" width={22} />
          <Typography variant="body1">{benefit}</Typography>
        </Box>
      ))}
    </Box>

    {item.content.buttons?.length > 0 && (
      <Box className="flex flex-wrap gap-4">
        {item.content.buttons.map((button, index) => (
          <Button
            key={index}
            component={RouterLink}
            href={button.link}
            variant="outlined"
            color="primary"
            className="font-medium text-md"
          >
            {button.text}
          </Button>
        ))}
      </Box>
    )}
  </Box>
);
